import { assertUnreachable } from "@arti/utilities";

export type TooltipAlign = "center" | "end" | "start";
export type TooltipSide = "bottom" | "left" | "right" | "top";

export interface TooltipPosition {
  arrowLeft?: number;
  arrowTop?: number;
  left: number;
  side: TooltipSide;
  top: number;
}

interface RectLike {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
}

interface TooltipViewport {
  height: number;
  width: number;
}

interface TooltipPlacementOptions {
  align: TooltipAlign;
  offset: number;
  preferredSide: TooltipSide;
  viewport: TooltipViewport;
}

const ARROW_EDGE_PADDING = 16;
const VIEWPORT_PADDING = 12;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getOppositeSide(side: TooltipSide): TooltipSide {
  switch (side) {
    case "top": {
      return "bottom";
    }
    case "bottom": {
      return "top";
    }
    case "left": {
      return "right";
    }
    case "right": {
      return "left";
    }
    default: {
      throw assertUnreachable(side);
    }
  }
}

function getCrossAxisStart(
  align: TooltipAlign,
  triggerStart: number,
  triggerSize: number,
  tooltipSize: number,
): number {
  switch (align) {
    case "start": {
      return triggerStart;
    }
    case "end": {
      return triggerStart + triggerSize - tooltipSize;
    }
    case "center": {
      return triggerStart + (triggerSize - tooltipSize) / 2;
    }
    default: {
      throw assertUnreachable(align);
    }
  }
}

function getResolvedSide(
  triggerRect: RectLike,
  tooltipRect: Pick<RectLike, "height" | "width">,
  preferredSide: TooltipSide,
  offset: number,
  viewport: TooltipViewport,
): TooltipSide {
  const availableSpace = {
    bottom: viewport.height - triggerRect.bottom,
    left: triggerRect.left,
    right: viewport.width - triggerRect.right,
    top: triggerRect.top,
  } satisfies Record<TooltipSide, number>;

  const requiredSpace = {
    bottom: tooltipRect.height + offset + VIEWPORT_PADDING,
    left: tooltipRect.width + offset + VIEWPORT_PADDING,
    right: tooltipRect.width + offset + VIEWPORT_PADDING,
    top: tooltipRect.height + offset + VIEWPORT_PADDING,
  } satisfies Record<TooltipSide, number>;

  if (availableSpace[preferredSide] >= requiredSpace[preferredSide]) {
    return preferredSide;
  }

  const oppositeSide = getOppositeSide(preferredSide);
  if (availableSpace[oppositeSide] >= requiredSpace[oppositeSide]) {
    return oppositeSide;
  }

  const sideWithMostSpace = Object.entries(availableSpace).toSorted(
    (left, right) => right[1] - left[1],
  )[0]?.[0];

  switch (sideWithMostSpace) {
    case "top":
    case "bottom":
    case "left":
    case "right": {
      return sideWithMostSpace;
    }
    default: {
      return preferredSide;
    }
  }
}

function getArrowCrossAxisOffset(
  triggerCenter: number,
  tooltipStart: number,
  tooltipSize: number,
): number {
  return clamp(
    triggerCenter - tooltipStart,
    ARROW_EDGE_PADDING,
    tooltipSize - ARROW_EDGE_PADDING,
  );
}

function withArrowPosition(
  triggerRect: RectLike,
  tooltipRect: Pick<RectLike, "height" | "width">,
  position: Omit<TooltipPosition, "arrowLeft" | "arrowTop">,
): TooltipPosition {
  switch (position.side) {
    case "top":
    case "bottom": {
      return {
        ...position,
        arrowLeft: getArrowCrossAxisOffset(
          triggerRect.left + triggerRect.width / 2,
          position.left,
          tooltipRect.width,
        ),
      };
    }
    case "left":
    case "right": {
      return {
        ...position,
        arrowTop: getArrowCrossAxisOffset(
          triggerRect.top + triggerRect.height / 2,
          position.top,
          tooltipRect.height,
        ),
      };
    }
    default: {
      throw assertUnreachable(position.side);
    }
  }
}

export function getTooltipPosition(
  triggerRect: RectLike,
  tooltipRect: Pick<RectLike, "height" | "width">,
  options: TooltipPlacementOptions,
): TooltipPosition {
  const { align, offset, preferredSide, viewport } = options;
  const resolvedSide = getResolvedSide(
    triggerRect,
    tooltipRect,
    preferredSide,
    offset,
    viewport,
  );

  switch (resolvedSide) {
    case "top": {
      return withArrowPosition(triggerRect, tooltipRect, {
        left: clamp(
          getCrossAxisStart(
            align,
            triggerRect.left,
            triggerRect.width,
            tooltipRect.width,
          ),
          VIEWPORT_PADDING,
          viewport.width - tooltipRect.width - VIEWPORT_PADDING,
        ),
        side: resolvedSide,
        top: Math.max(
          VIEWPORT_PADDING,
          triggerRect.top - tooltipRect.height - offset,
        ),
      });
    }
    case "bottom": {
      return withArrowPosition(triggerRect, tooltipRect, {
        left: clamp(
          getCrossAxisStart(
            align,
            triggerRect.left,
            triggerRect.width,
            tooltipRect.width,
          ),
          VIEWPORT_PADDING,
          viewport.width - tooltipRect.width - VIEWPORT_PADDING,
        ),
        side: resolvedSide,
        top: Math.min(
          viewport.height - tooltipRect.height - VIEWPORT_PADDING,
          triggerRect.bottom + offset,
        ),
      });
    }
    case "left": {
      return withArrowPosition(triggerRect, tooltipRect, {
        left: Math.max(
          VIEWPORT_PADDING,
          triggerRect.left - tooltipRect.width - offset,
        ),
        side: resolvedSide,
        top: clamp(
          getCrossAxisStart(
            align,
            triggerRect.top,
            triggerRect.height,
            tooltipRect.height,
          ),
          VIEWPORT_PADDING,
          viewport.height - tooltipRect.height - VIEWPORT_PADDING,
        ),
      });
    }
    case "right": {
      return withArrowPosition(triggerRect, tooltipRect, {
        left: Math.min(
          viewport.width - tooltipRect.width - VIEWPORT_PADDING,
          triggerRect.right + offset,
        ),
        side: resolvedSide,
        top: clamp(
          getCrossAxisStart(
            align,
            triggerRect.top,
            triggerRect.height,
            tooltipRect.height,
          ),
          VIEWPORT_PADDING,
          viewport.height - tooltipRect.height - VIEWPORT_PADDING,
        ),
      });
    }
    default: {
      throw assertUnreachable(resolvedSide);
    }
  }
}
